import React, { useEffect, useMemo, useState } from 'react';

import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useField } from 'formik';
import { Autocomplete, AutocompleteProps } from 'formik-mui';
import { useSnackbar } from 'notistack';
import { useDebounce } from 'use-debounce';

import { CissTextField } from 'components/fields/';
import { RequestListagemProps, RequestOptionsType, useRequestListagem } from 'hooks/useRequestListagem';

interface MotivoProps {
    dsMotivoDevolucao: string;
    fgAtivo: boolean;
    idMotivoDevolucao: number;
}

interface RequestListagemMotivoes extends RequestListagemProps {
    data: {
        data: MotivoProps[];
    };
}

interface MotivoFieldProps extends AutocompleteProps<MotivoProps, boolean | undefined, boolean | undefined, boolean | undefined> {
    multiple?: boolean;
    label: string;
}

export function MotivoFieldFormik({ onChange, multiple = true, label = 'Motivo da devolução', ...PropsFormik }: MotivoFieldProps) {
    const [open, setOpen] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState<string>('');
    const [query, setQuery] = useState('');
    const { enqueueSnackbar } = useSnackbar();
    const RequestListagem = useRequestListagem();
    const [field, { error }] = useField(PropsFormik.field.name);
    const [queryValue] = useDebounce(query, 500);

    const requestOptions: RequestOptionsType = useMemo(
        () => ({
            url: '/gestao/motivodevolucao',
            query: queryValue,
            sort: [{ property: 'dsMotivoDevolucao', direction: 'ASC' }],
            limit: 50,
        }),
        [queryValue],
    );

    const { isLoading, fetchStatus, data } = useQuery(
        [requestOptions],
        () => {
            const request: Promise<RequestListagemMotivoes> = RequestListagem(requestOptions);
            return request;
        },
        {
            keepPreviousData: false,
            enabled: open,
            onError: () => {
                enqueueSnackbar('Falha ao processar solicitação de Motivoes', { variant: 'error' });
            },
        },
    );

    useEffect(() => {
        if (open) {
            setQuery(inputValue);
        }
    }, [inputValue, open]);

    return (
        <Autocomplete
            {...PropsFormik}
            multiple={multiple}
            onChange={onChange}
            loading={isLoading && fetchStatus === 'fetching'}
            options={data?.data?.data ?? []}
            isOptionEqualToValue={(option, value) => option.idMotivoDevolucao === value.idMotivoDevolucao}
            getOptionLabel={(option: MotivoProps | string) => {
                if (typeof option === 'string') {
                    return option;
                }

                return option.dsMotivoDevolucao;
            }}
            renderInput={(params) => <CissTextField label={label} error={Boolean(error)} helperText={error} {...params} />}
            onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
            }}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            renderOption={(props, option) => (
                <Box component="li" {...props} key={option.idMotivoDevolucao}>
                    {option.idMotivoDevolucao} - {option.dsMotivoDevolucao}
                </Box>
            )}
        />
    );
}
